import { useEffect, useState } from "react";
import {
  LESSON_DURATION_MAX,
  LESSON_DURATION_MIN,
  type PlanSummary,
  type ScheduleEntry,
} from "@edu/shared";
import * as api from "../../lib/api.ts";

/**
 * Форма индивидуальной правки занятия: своё название, своя длительность, своя
 * заготовка на один четверг, а не на всю серию.
 *
 * Своим файлом, а не внутри `EntryTools.tsx`: вместе они давали 513 строк при
 * потолке в 400, и форма это единственный кусок карточки, который ходит по
 * сети сам (список заготовок).
 *
 * ⚠️ Пустое поле означает «как у серии», а не «пусто». Поэтому и уезжает оно
 * как `null`: у сервера отсутствие поля значит «не трогать», а `null` значит
 * «вернуть как у серии», и форма, которая шлёт все три поля разом, обязана
 * говорить именно вторым языком.
 *
 * ⚠️ Заготовки грузятся при ОТКРЫТИИ формы, а не вместе с расписанием. Их
 * список нужен одному человеку в одну секунду, а неделя расписания рисуется
 * всем и всегда: тянуть его заранее значит платить кругом по сети на канале
 * 128 кбит/с за то, чего обычно не открывают.
 */
export function OverrideForm({
  entry,
  busy,
  run,
  onDone,
  slotId,
  occursOn,
}: {
  entry: ScheduleEntry;
  busy: boolean;
  run: (fn: () => Promise<void>) => Promise<void>;
  onDone: () => void;
  slotId: string;
  occursOn: string;
}) {
  const o = entry.override;
  const [title, setTitle] = useState(o?.title ?? "");
  const [duration, setDuration] = useState(
    o?.durationMin === undefined || o.durationMin === null ? "" : String(o.durationMin),
  );
  const [planId, setPlanId] = useState(o?.planId ?? "");
  const [plans, setPlans] = useState<PlanSummary[] | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .listPlans()
      .then((p) => {
        if (alive) setPlans(p.plans);
      })
      // Отказ проглатывается: без списка заготовок форма всё равно умеет
      // поменять название и длительность, а красная строка тут говорила бы о
      // поломке, которой для этого дела нет
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const save = (clear: boolean) =>
    void run(async () => {
      await api.patchOccurrence(slotId, occursOn, {
        title: clear || title.trim() === "" ? null : title.trim(),
        durationMin: clear || duration.trim() === "" ? null : Number(duration),
        planId: clear || planId === "" ? null : planId,
      });
      onDone();
    });

  return (
    <div className="form">
      <p className="muted">
        Правка только этого занятия. Серия и остальные её занятия не меняются.
      </p>

      <div className="form form--row">
        <label className="field">
          <span className="lbl">Название</span>
          <input
            className="fld"
            value={title}
            placeholder="как у серии"
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="lbl">Минут</span>
          <input
            className="fld"
            type="number"
            min={LESSON_DURATION_MIN}
            max={LESSON_DURATION_MAX}
            value={duration}
            placeholder="как у серии"
            onChange={(e) => setDuration(e.target.value)}
          />
        </label>
      </div>

      <label className="field">
        <span className="lbl">Заготовка</span>
        <select
          className="fld"
          value={planId}
          onChange={(e) => setPlanId(e.target.value)}
          disabled={plans === null}
        >
          <option value="">{plans === null ? "загрузка…" : "как у серии"}</option>
          {(plans ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.title}
            </option>
          ))}
        </select>
      </label>

      <div className="form__foot">
        {/*
          «Вернуть как у серии» стирает правку, но обычным весом, а не красным:
          стёртое набирается заново теми же полями, что стоят выше, и потери в
          этом нет никакой
        */}
        {entry.customized && (
          <button type="button" className="btn" disabled={busy} onClick={() => save(true)}>
            Вернуть как у серии
          </button>
        )}
        <button type="button" className="btn btn--quiet" disabled={busy} onClick={onDone}>
          Не надо
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => save(false)}>
          Сохранить
        </button>
      </div>

      {/*
        Материал берётся при ПЕРВОМ открытии доски. До него смена заготовки
        бесплатна, после него сервер откажет: слайды уже разложены, и подмена
        ссылки их не тронет
      */}
      <p className="muted">
        {entry.lessonId === null
          ? "Урока ещё нет: заготовку возьмёт кнопка «Подготовить»."
          : "Урок уже подготовлен: название и заготовка поедут в него. Если доску урока уже открывали, заготовку сменить нельзя."}
      </p>
    </div>
  );
}
